import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Activity, Clock, ArrowRight, TrendingUp } from "lucide-react";
import { api, formatError } from "../lib/api.js";
import StatusBadge from "../components/StatusBadge.jsx";
import Sparkline from "../components/Sparkline.jsx";

const WINDOWS = [
  { value: 7, label: "7 dias" },
  { value: 30, label: "30 dias" },
  { value: 90, label: "90 dias" },
  { value: 365, label: "1 ano" },
];

function MetricCard({ label, value, hint, accent = "navy", testid }) {
  const colors = {
    navy: "border-l-[#1F3864]",
    amber: "border-l-[#B45309]",
    green: "border-l-[#28A745]",
    red: "border-l-[#DC3545]",
    grey: "border-l-[#6C757D]",
  };
  return (
    <div
      className={`bg-white border border-gray-200 border-l-4 ${colors[accent]} rounded-md p-4`}
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-widest text-[#6C757D]">{label}</div>
      <div className="font-heading text-2xl font-bold text-[#1A1A2E] mt-1">{value}</div>
      {hint && <div className="text-xs text-[#6C757D] mt-1">{hint}</div>}
    </div>
  );
}

function HoursLabel({ hours, samples }) {
  if (hours == null) return <span className="text-[#94A3B8] font-mono">—</span>;
  const display =
    hours < 1 ? `${Math.round(hours * 60)} min` : hours < 48 ? `${hours.toFixed(1)} h` : `${(hours / 24).toFixed(1)} d`;
  return (
    <>
      {display}
      {samples != null && <span className="text-[10px] text-[#94A3B8] ml-1">(n={samples})</span>}
    </>
  );
}

function TrendCard({ title, values, color, formatValue, unit, testid }) {
  const numeric = values.filter((v) => v != null && !Number.isNaN(v));
  const total = values.reduce((a, b) => a + (b ?? 0), 0);
  const hasLatency = title.toLowerCase().startsWith("latência");
  return (
    <div
      className="border border-gray-200 rounded-md p-3 bg-[#FAFBFC]"
      data-testid={testid}
    >
      <div className="flex items-baseline justify-between gap-2 mb-1.5">
        <span className="text-[11px] uppercase tracking-widest text-[#6C757D]">{title}</span>
        <span className="text-[10px] text-[#94A3B8]">{unit}</span>
      </div>
      <Sparkline values={values} color={color} formatValue={formatValue} width={280} height={56} />
      {!hasLatency && (
        <div className="text-[10px] text-[#94A3B8] mt-1">Total 12 semanas: {total}</div>
      )}
      {hasLatency && numeric.length === 0 && (
        <div className="text-[10px] text-[#94A3B8] mt-1">Sem amostras suficientes</div>
      )}
    </div>
  );
}

function Bar({ value, max, color }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="h-1.5 rounded-sm bg-[#F4F6F8] overflow-hidden">
      <div className="h-full" style={{ width: `${pct}%`, backgroundColor: color }} />
    </div>
  );
}

export default function FlowAnalyticsPage() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    setData(null);
    setErr("");
    api
      .get(`/admin/flow-analytics?days=${days}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(formatError(e.response?.data?.detail) || "Falha a carregar"));
  }, [days]);

  if (err) {
    return (
      <div className="p-6 max-w-[1600px] mx-auto">
        <div className="bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm p-4">{err}</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6 max-w-[1600px] mx-auto flex items-center gap-2 text-[#6C757D]">
        <Loader2 className="w-4 h-4 animate-spin" /> A carregar análise…
      </div>
    );
  }

  const { counts, latencies_hours, pcp_outcomes, pcp_rates, pec_status_breakdown, bottlenecks_by_status, top_bottlenecks, trends } = data;
  const pcpOutcomesEntries = Object.entries(pcp_outcomes).sort((a, b) => b[1] - a[1]);
  const pcpOutcomesMax = Math.max(1, ...pcpOutcomesEntries.map(([, v]) => v));
  const pecBreakdownEntries = Object.entries(pec_status_breakdown).sort((a, b) => b[1] - a[1]);
  const pecBreakdownMax = Math.max(1, ...pecBreakdownEntries.map(([, v]) => v));
  const bottleneckStatuses = Object.keys(bottlenecks_by_status).sort(
    (a, b) =>
      (bottlenecks_by_status[b][0]?.hours_in_state || 0) -
      (bottlenecks_by_status[a][0]?.hours_in_state || 0)
  );

  return (
    <div className="p-4 sm:p-6 max-w-[1600px] mx-auto" data-testid="flow-analytics-page">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-[#1A1A2E] flex items-center gap-2">
            <Activity className="w-6 h-6 text-[#1F3864]" /> Análise de fluxo
          </h1>
          <p className="text-sm text-[#6C757D] mt-1">
            Métricas operacionais SOM → PCP → PEC nos últimos {days} dias.
          </p>
        </div>
        <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-sm p-0.5">
          {WINDOWS.map((w) => (
            <button
              key={w.value}
              onClick={() => setDays(w.value)}
              className={`px-3 py-1 text-xs rounded-sm font-medium ${
                w.value === days ? "bg-[#1F3864] text-white" : "text-[#1A1A2E] hover:bg-[#F4F6F8]"
              }`}
              data-testid={`flow-window-${w.value}`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <MetricCard
          label="SOMs abertas"
          value={counts.soms_open}
          hint={`${counts.soms_in_window} criadas em ${days}d · ${counts.soms_closed} encerradas`}
          accent="navy"
          testid="card-soms-open"
        />
        <MetricCard
          label="PCPs no sistema"
          value={counts.pcps_total}
          hint={`${pcp_rates.pcps_finished} concluídas · ${pcp_rates.alidata_quotation_rate_pct}% → Alidata`}
          accent="amber"
          testid="card-pcps"
        />
        <MetricCard
          label="PECs no sistema"
          value={counts.pecs_total}
          hint={`${counts.pecs_from_pcp} originadas de PCP (${pcp_rates.pecs_from_pcp_rate_pct}%)`}
          accent="green"
          testid="card-pecs"
        />
        <MetricCard
          label="Devolvidas ao SA"
          value={`${pcp_rates.returned_to_advisor_rate_pct}%`}
          hint="Quanto da PCP voltou para revisão do service advisor"
          accent="red"
          testid="card-returned-rate"
        />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-white border border-gray-200 rounded-md p-4">
          <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-[#1A1A2E] mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#1F3864]" /> Latências médias
          </h3>
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[#1A1A2E]">
                SOM → 1ª PCP criada
              </span>
              <span className="font-mono font-semibold text-[#1F3864]" data-testid="lat-som-pcp">
                <HoursLabel hours={latencies_hours.som_to_first_pcp_avg} samples={latencies_hours.som_to_first_pcp_samples} />
              </span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-[#1A1A2E]">
                PCP → Devolvido ao service advisor
              </span>
              <span className="font-mono font-semibold text-[#1F3864]" data-testid="lat-pcp-quote">
                <HoursLabel hours={latencies_hours.pcp_to_alidata_quotation_avg} samples={latencies_hours.pcp_to_alidata_quotation_samples} />
              </span>
            </div>
          </div>
          <p className="text-[10px] text-[#94A3B8] mt-3">
            n = nº de registos usados para a média. Apenas SOMs/PCPs criadas nos últimos {days} dias entram em SOM→PCP.
          </p>
        </div>

        <div className="bg-white border border-gray-200 rounded-md p-4">
          <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-[#1A1A2E] mb-3 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#B45309]" /> Resultados das PCPs
          </h3>
          <div className="space-y-2">
            {pcpOutcomesEntries.length === 0 && <div className="text-sm text-[#6C757D]">Sem PCPs registadas.</div>}
            {pcpOutcomesEntries.map(([status, n]) => (
              <div key={status} data-testid={`pcp-outcome-${status}`}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-[#1A1A2E]">{status}</span>
                  <span className="font-mono font-semibold text-[#1F3864]">{n}</span>
                </div>
                <Bar value={n} max={pcpOutcomesMax} color="#B45309" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white border border-gray-200 rounded-md p-4 mb-6">
        <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-[#1A1A2E] mb-3">
          Pipeline de PEC — distribuição por estado actual
        </h3>
        <div className="space-y-2">
          {pecBreakdownEntries.length === 0 && <div className="text-sm text-[#6C757D]">Sem PEC registadas.</div>}
          {pecBreakdownEntries.map(([status, n]) => (
            <div key={status} data-testid={`pec-status-${status}`}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-[#1A1A2E]">{status}</span>
                <span className="font-mono font-semibold text-[#1F3864]">{n}</span>
              </div>
              <Bar value={n} max={pecBreakdownMax} color="#28A745" />
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white border border-gray-200 rounded-md p-4 mb-6" data-testid="trends-section">
        <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-[#1A1A2E] mb-3">
          Tendência semanal — últimas 12 semanas
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <TrendCard
            title="SOMs criadas"
            values={trends.soms_created}
            color="#1F3864"
            formatValue={(v) => `${v}`}
            unit="por semana"
            testid="trend-soms"
          />
          <TrendCard
            title="PCPs criadas"
            values={trends.pcps_created}
            color="#B45309"
            formatValue={(v) => `${v}`}
            unit="por semana"
            testid="trend-pcps"
          />
          <TrendCard
            title="PECs criadas"
            values={trends.pecs_created}
            color="#28A745"
            formatValue={(v) => `${v}`}
            unit="por semana"
            testid="trend-pecs"
          />
          <TrendCard
            title="Latência SOM → 1ª PCP"
            values={trends.som_to_pcp_avg_hours}
            color="#0D9488"
            formatValue={(v) => (v < 48 ? `${v.toFixed(1)} h` : `${(v / 24).toFixed(1)} d`)}
            unit="média semanal"
            testid="trend-som-pcp"
            invertDelta
          />
          <TrendCard
            title="Latência PCP → Orçamento Alidata"
            values={trends.pcp_to_quote_avg_hours}
            color="#7C3AED"
            formatValue={(v) => (v < 48 ? `${v.toFixed(1)} h` : `${(v / 24).toFixed(1)} d`)}
            unit="média semanal"
            testid="trend-pcp-quote"
            invertDelta
          />
        </div>
        <div className="text-[10px] text-[#94A3B8] mt-3">
          Cada ponto representa uma semana ISO. Lacunas correspondem a semanas sem amostras.
        </div>
      </section>

      <section className="bg-white border border-gray-200 rounded-md p-4" data-testid="bottlenecks">
        <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-[#1A1A2E] mb-3">
          SOMs mais paradas (Top {top_bottlenecks.length})
        </h3>
        {top_bottlenecks.length === 0 ? (
          <div className="text-sm text-[#6C757D]">Sem SOMs abertas.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-[#6C757D] border-b border-gray-200">
              <tr>
                <th className="px-2 py-2 text-left">SOM</th>
                <th className="px-2 py-2 text-left">Cliente</th>
                <th className="px-2 py-2 text-left">Filial</th>
                <th className="px-2 py-2 text-left">Estado actual</th>
                <th className="px-2 py-2 text-left">Prioridade</th>
                <th className="px-2 py-2 text-right">Há quanto tempo</th>
              </tr>
            </thead>
            <tbody>
              {top_bottlenecks.map((b) => (
                <tr key={b.id} className="border-b border-gray-100 hover:bg-[#F8F9FA]" data-testid={`bottleneck-${b.id}`}>
                  <td className="px-2 py-2 font-mono">
                    <Link to={`/service-cases/${b.id}`} className="text-[#1F3864] font-semibold hover:underline">
                      {b.number}
                    </Link>
                  </td>
                  <td className="px-2 py-2">{b.customer_name}</td>
                  <td className="px-2 py-2 text-[#6C757D]">{b.branch}</td>
                  <td className="px-2 py-2"><StatusBadge status={b.status} /></td>
                  <td className="px-2 py-2 text-[#6C757D]">{b.priority || "—"}</td>
                  <td className="px-2 py-2 text-right font-mono">
                    {b.hours_in_state == null
                      ? "—"
                      : b.hours_in_state < 24
                      ? `${b.hours_in_state.toFixed(1)} h`
                      : `${(b.hours_in_state / 24).toFixed(1)} d`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {bottleneckStatuses.length > 1 && (
        <section className="mt-4 bg-white border border-gray-200 rounded-md p-4">
          <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-[#1A1A2E] mb-3">
            Distribuição por estado (todas as SOMs abertas)
          </h3>
          <div className="flex flex-wrap gap-2">
            {bottleneckStatuses.map((s) => (
              <div
                key={s}
                className="inline-flex items-center gap-2 border border-gray-200 rounded-sm px-2 py-1 text-xs"
                data-testid={`status-pill-${s}`}
              >
                <StatusBadge status={s} />
                <span className="font-mono text-[#1F3864] font-semibold">{bottlenecks_by_status[s].length}</span>
                <ArrowRight className="w-3 h-3 text-[#94A3B8]" />
                <span className="text-[#6C757D]">
                  máx {((bottlenecks_by_status[s][0]?.hours_in_state || 0) / 24).toFixed(1)} d
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
